import React, { Fragment } from 'react';
import { useGate } from 'statsig-react';
import { TotalPointsBadge } from '@components/Points/TotalPointsBadge';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { useHeaderStore } from '@store/header';
import { FEATURE_GATES, NAV_LINKS } from '@utils/constants';
import { useShouldShowReferralEarningsIndicator } from '../Notification';
import { LinkIndicator, NavLink, NewNavLink } from '../styles';
import { CollectorSignedInOptions } from './CollectorSignedInOptions';
import { ForArtists } from './ForArtists';

type NavMenuOptions = {
  title: string;
  key: string;
  href: Pathname;
  hideMobile?: boolean;
  notificationIndicator?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
};

const ADMIN_OR_ARTIST_RELATIONS_OPTIONS: Array<NavMenuOptions> = [
  {
    title: 'Admin',
    key: 'admin',
    href: '/admin',
  },
  {
    title: 'Artist Applications',
    key: 'artist-application',
    href: NAV_LINKS.AdminArtistApplications,
  },
];

const ARTIST_OPTIONS_WITH_PROFILE: Array<NavMenuOptions> = [
  {
    title: 'My Releases',
    key: 'artist-releases',
    href: '/dashboard/releases',
  },
];

export const NavLinks = ({ variant }: { variant: 'desktop' | 'mobile' }) => {
  const { authUser, isConnectingAPIUser } = useAuth();
  const { value: pointsEnabled } = useGate(FEATURE_GATES.SOCIALS_POINTS);

  const { close } = useHeaderStore();

  const isEitherAdminOrRelations = !!authUser?.roles.isAdmin || !!authUser?.roles.isArtistRelations;
  const hasArtistRole = !!authUser?.artist;

  const adminOrArtistRelationsMenuOptions = ADMIN_OR_ARTIST_RELATIONS_OPTIONS.map(option => {
    return (
      <Fragment key={option.key}>
        {isConnectingAPIUser ? (
          <div className="h-8 w-full animate-pulse rounded-sm bg-base200 md:h-6 md:w-36" />
        ) : (
          <AccessibleLink absoluteUri={option.href} type="external" onClick={close}>
            <NavLink variant={variant}>{option.title}</NavLink>
          </AccessibleLink>
        )}
      </Fragment>
    );
  });

  const shouldShowReferralEarningsIndicator = useShouldShowReferralEarningsIndicator();

  const WITHDRAW_OPTIONS: Array<NavMenuOptions> = [
    {
      title: 'My Earnings',
      key: 'withdraw',
      href: '/dashboard/earnings',
      notificationIndicator: shouldShowReferralEarningsIndicator,
    },
  ];

  const ARTIST_DASHBOARD: Array<NavMenuOptions> = [
    {
      title: 'Artist Dashboard',
      key: 'artist-dashboard',
      href: '/dashboard/releases',
    },
  ];

  const artistWithProfileOptions =
    variant === 'desktop'
      ? [...ARTIST_DASHBOARD]
      : [...ARTIST_OPTIONS_WITH_PROFILE, ...WITHDRAW_OPTIONS];

  const artistOptionsToShow = hasArtistRole
    ? artistWithProfileOptions.filter(({ hideMobile }) => {
        if (variant === 'mobile' && hideMobile) {
          return false;
        }
        return true;
      })
    : [];

  const artistMenuOptions = artistOptionsToShow.map(option => {
    const pathnameUri = option.href;

    return (
      <Fragment key={option.key}>
        {isConnectingAPIUser ? (
          <div className="h-8 w-full animate-pulse rounded-sm bg-base200 md:h-6 md:w-36" />
        ) : (
          <AccessibleLink pathnameUri={pathnameUri} onClick={close}>
            <NewNavLink>
              {' '}
              {option.icon}
              {option.title}
              {option.notificationIndicator && <LinkIndicator variant={variant} />}
            </NewNavLink>
          </AccessibleLink>
        )}
      </Fragment>
    );
  });

  return (
    <>
      <Divider orientation="horizontal" className="!mb-2" />
      {pointsEnabled && (
        <>
          <AccessibleLink
            pathnameUri={NAV_LINKS.Leaderboard}
            onClick={close}
            className="flex w-full items-center justify-between md:hidden"
          >
            <NewNavLink>Leaderboard</NewNavLink>
            {/* TODO: integrate */}
            <TotalPointsBadge totalPoints={1800} size="sm" />
          </AccessibleLink>
        </>
      )}
      {artistMenuOptions}
      {hasArtistRole && (
        <AccessibleLink pathnameUri={NAV_LINKS.Community} onClick={close} className="md:hidden">
          <NewNavLink>My Community</NewNavLink>
        </AccessibleLink>
      )}
      <CollectorSignedInOptions variant={variant} />

      {isEitherAdminOrRelations && (
        <>
          <Divider orientation="horizontal" className="!mb-0" />
          {adminOrArtistRelationsMenuOptions}
        </>
      )}
      {!hasArtistRole && <ForArtists navLoading={isConnectingAPIUser} closeNav={close} />}
    </>
  );
};
